<template>
  <v-card class="acessos-por-pagina-card card-title pa-0 pr-3 pl-3">
    <v-container class="pa-0 card-header" fluid>
      <v-card-title class="pa-3 title">Usuários ativos por loja</v-card-title>
      <v-card-subtitle class="pa-3 subtitle mf-subtitle-1">Quantidade de usuários logados e não logados que acessaram o aplicativo por loja.</v-card-subtitle>
    </v-container>
    <mf-loading loader="loader" loader-type="table">
      <v-container v-if="series[0].data.length > 0" fluid class="table-container pa-0 pr-4 pl-4">
        <apex-chart type="bar" :options="chartOptions" height="350" :width="chartWidth" :series="series" />
      </v-container>
      <empty-chart v-if="series[0].data.length === 0" />
      <v-container v-if="series[0].data.length > 0" fluid class="pa-0 navigator-container">
        <chart-navigator
          :selectedPeriod="chartPeriod"
          :chart-index="chartIndex"
          :metrics-length="metricsLength"
          :metrics-period="metricsPeriod"
          @navigateTo="navigateTo"
        />
      </v-container>
    </mf-loading>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import EmptyChart from '@/modules/mobile/desempenho/views/components/EmptyChart'
import { colors } from '@/config/colors'
import { QUERY_GET_APP_ACCESSES_BY_UNIT } from '@/modules/mobile/desempenho/graphql'
import ChartNavigator from '@/components/ChartNavigator'
import getAppMetricsNavigatorPeriod from '@/services/getAppMetricsNavigatorPeriod'

export default {
  components: {
    ApexChart: VueApexCharts,
    EmptyChart,
    ChartNavigator,
  },
  props: {
    metricsPeriod: String,
  },
  data: () => ({
    chartIndex: 0,
  }),
  computed: {
    sortedMetrics() {
      if (!this.appActiveUsersByUnit || this.appActiveUsersByUnit.length === 0) {
        return []
      }
      return [...this.appActiveUsersByUnit[this.chartIndex].metrics].sort((a, b) => a.unit_users_count - b.unit_users_count).reverse()
    },
    series() {
      return [
        {
          name: 'Usuários ativos por loja',
          data: this.sortedMetrics.map((item) => item.unit_users_count),
        },
      ]
    },
    chartCategories() {
      return this.sortedMetrics.map((item) => {
        let unitName = item.unit.name
        if (unitName.length > 18) {
          unitName = unitName.split(' ').reduce((acc, item) => {
            if (acc.length > 0 && acc[acc.length - 1]?.length + item.length <= 18) {
              acc[acc.length - 1] = `${acc[acc.length - 1]} ${item}`
            } else {
              acc.push(item)
            }
            return acc.slice(0, 3)
          }, [])
        }
        return unitName
      })
    },
    chartWidth() {
      let chartWidth = this.series[0].data.length * 150
      if (chartWidth < 1300) {
        chartWidth = 1300
      }
      return chartWidth
    },
    metricsLength() {
      if (!this.appActiveUsersByUnit) return 0
      return this.appActiveUsersByUnit.length - 1
    },
    chartPeriod() {
      const currentPeriod =
        this.metricsPeriod === 'MONTHLY' ? this.appActiveUsersByUnit[this.chartIndex].month : this.appActiveUsersByUnit[this.chartIndex].year
      const { navigatorPeriod } = getAppMetricsNavigatorPeriod(this.metricsPeriod, currentPeriod)
      return navigatorPeriod
    },
    chartOptions() {
      return {
        chart: {
          height: 350,
          type: 'bar',
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            columnWidth: this.series[0].data.length > 5 ? '20px' : this.series[0].data.length > 2 ? '10px' : '5px',
            borderRadius: 3,
            horizontal: false,
            dataLabels: {
              position: 'top',
            },
          },
        },
        dataLabels: {
          enabled: true,
          position: 'top',
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ['#F9FAFB'],
            fontWeight: 'normal',
          },
          background: {
            enabled: true,
            foreColor: '#969EA7',
            padding: 4,
            borderRadius: 2,
          },
          formatter(val) {
            const formattedValue = val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val
            return formattedValue
          },
        },
        xaxis: {
          categories: this.chartCategories,
          labels: {
            show: true,
            align: 'center',
            minWidth: 0,
            maxWidth: 200,
            style: {
              colors: '#969EA7',
              fontSize: '12px',
            },
          },
          axisBorder: {
            show: true,
          },
        },
        yaxis: {
          labels: {
            formatter: function (val, index) {
              if (!val) return
              return val.toLocaleString()
            },
          },
        },
        tooltip: {
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const registers = series[0][dataPointIndex]

            return `
              <v-card class="d-flex flex-row justify-start pa-1">
                <span><strong>${registers.toLocaleString()} </strong>${registers > 1 || registers === 0 ? 'usuários' : 'usuário'}</span>
              </v-card>
            `
          },
        },
        fill: {
          type: 'gradient',
          colors: colors[0],
        },
        grid: {
          yaxis: {
            lines: {
              show: true,
            },
          },
          padding: {
            top: 0,
            right: 10,
            bottom: 0,
            left: 10,
          },
        },
      }
    },
  },
  apollo: {
    appActiveUsersByUnit: {
      query: QUERY_GET_APP_ACCESSES_BY_UNIT,
      variables() {
        return { metricsPeriod: this.metricsPeriod }
      },
    },
  },
  watch: {
    metricsPeriod() {
      this.chartIndex = 0
    },
  },
  methods: {
    navigateTo(newIndex) {
      this.chartIndex = newIndex
    },
  },
}
</script>

<style lang="scss" scoped>
.acessos-por-pagina-card {
  height: 500px;
  position: relative;
}
.card-header {
  height: 100px;
}
p {
  color: $cinza-d20;
  font-size: 12px;
  margin: 0;
}
.table-container {
  overflow-x: scroll;
  height: 400px;
}
.table-text {
  color: $cinza-d20;
}
.label-text {
  word-wrap: break-word;
}
.navigator-container {
  position: absolute;
  bottom: 20px;
  right: 12px;
}
</style>
